import { logger } from '../log';
import type { OptionCallPut } from '../types';
import { initialState } from './reducer';
import type { InstrumentType, FocusedElement, SymbolPickerState, DropdownState } from './types';
import { DateTimeFormatter, LocalDate } from '@js-joda/core';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import {
    DATE_FORMATTER,
    getUnderlyingSymbolFromInstrument,
    instrumentToString,
    parseInstrument,
} from 'src/lib/instrumentUtils';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';

const log = logger.getSubLogger({ name: 'utils' });

// Helper function to format dates for display (YYMMDD to YYYY-MM-DD)
export function formatDisplayDate(date: string): string {
    if (!date || date.length !== 6) return date;
    const year = `20${date.substring(0, 2)}`;
    const month = date.substring(2, 4);
    const day = date.substring(4, 6);
    return `${year}-${month}-${day}`;
}

// Filter functions for each dropdown type
export const filterPrices = (prices: number[], searchTerm: string): number[] => {
    if (!searchTerm) return prices;
    const searchNum = Number.parseFloat(searchTerm);
    return prices.filter(
        (price) => price.toString().startsWith(searchTerm) || (!isNaN(searchNum) && price === searchNum),
    );
};

export const filterDates = (dates: string[], searchTerm: string): string[] => {
    if (!searchTerm) return dates;
    return dates.filter((date) => {
        const displayDate = formatDisplayDate(date);
        return date.includes(searchTerm) || displayDate.includes(searchTerm);
    });
};

type SymbolLike = string | { symbol: string };
export const filterSymbols = (symbols: SymbolLike[], searchTerm: string): SymbolLike[] => {
    const upperTerm = searchTerm.toUpperCase();
    return symbols.filter((symbol) => {
        const symbolStr = typeof symbol === 'string' ? symbol : symbol.symbol;
        return symbolStr.toUpperCase().includes(upperTerm);
    });
};

// No filtering needed - instrument type is selected via button, not text input
export const filterInstrumentTypes = (types: InstrumentType[], _searchTerm: string): InstrumentType[] => {
    return types;
};

// No filtering needed - call/put is selected via button, not text input
export const filterOptionCallPut = (types: OptionCallPut[], _searchTerm: string): OptionCallPut[] => {
    return types;
};

// Get the appropriate filter function for each element
export function getFilterFunction(element: FocusedElement): ((items: any[], searchTerm: string) => any[]) | null {
    switch (element) {
        case 'instrumentType':
            return filterInstrumentTypes;
        case 'underlyingSymbol':
            return filterSymbols;
        case 'expirationDate':
            return filterDates;
        case 'optionCallPut':
            return filterOptionCallPut;
        case 'strikePrice':
            return filterPrices;
        default:
            return null;
    }
}

// Sort functions for each dropdown type
export const sortPrices = (prices: number[]): number[] => {
    return prices.toSorted((a, b) => a - b);
};

export const sortDates = (dates: string[]): string[] => {
    return dates.toSorted((a, b) => {
        try {
            const dateA = LocalDate.parse(a, DATE_FORMATTER);
            const dateB = LocalDate.parse(b, DATE_FORMATTER);
            return dateA.compareTo(dateB);
        } catch (_error) {
            // Fallback to string comparison for invalid dates
            return a.localeCompare(b);
        }
    });
};

export const sortSymbols = (symbols: SymbolLike[]): SymbolLike[] => {
    return symbols;
};

// No sorting needed - instrument type is selected via button, not dropdown
export const sortInstrumentTypes = (types: InstrumentType[]): InstrumentType[] => {
    return types;
};

// No sorting needed - call/put is selected via button, not dropdown
export const sortOptionCallPut = (types: OptionCallPut[]): OptionCallPut[] => {
    return types;
};

// Get the appropriate sort function for each element
export function getSortFunction(element: FocusedElement): ((items: any[]) => any[]) | null {
    switch (element) {
        case 'instrumentType':
            return sortInstrumentTypes;
        case 'underlyingSymbol':
            return sortSymbols;
        case 'expirationDate':
            return sortDates;
        case 'optionCallPut':
            return sortOptionCallPut;
        case 'strikePrice':
            return sortPrices;
        default:
            return null;
    }
}

// Helper function to get the next/previous focusable element based on current state and direction
export function getNextElement(
    currentElement: FocusedElement,
    instrumentType: InstrumentType,
    direction: 'forward' | 'backward',
): FocusedElement | null {
    const focusOrder: FocusedElement[] =
        instrumentType === 'equity'
            ? ['instrumentType', 'underlyingSymbol']
            : ['instrumentType', 'underlyingSymbol', 'expirationDate', 'optionCallPut', 'strikePrice'];

    const currentIndex = focusOrder.indexOf(currentElement);

    switch (direction) {
        case 'forward': {
            const nextIndex = currentIndex + 1;

            // when moving forward, if we are at the last element, we return null
            // so focus an escape the symbol picker group
            if (nextIndex >= focusOrder.length) return null;

            return focusOrder[nextIndex];
        }
        case 'backward': {
            const previousIndex = currentIndex - 1;

            // when moving backward, if we are at the first element, we return null
            // so focus an escape the symbol picker group
            if (previousIndex < 0) return null;

            return focusOrder[previousIndex];
        }
        default:
            throw new UnreachableCaseError(direction);
    }
}

// Helper function to validate the current state is syntactically correct
// and return the symbol string, or an empty string if the state is invalid
export function validateStateInstrumentAndProduceSymbolString(
    stateInstrument: SymbolPickerState['instrument'],
): string {
    const uiInstrument = stateToInstrument(stateInstrument);

    if (!uiInstrument) {
        return '';
    }

    try {
        return instrumentToString(uiInstrument) || '';
    } catch (error) {
        log.error('error converting stateInstrument to instrumentString', { error });
        return '';
    }
}

// Helper function to get element dropdown state (new centralized approach)
export function getElementDropdown(state: SymbolPickerState, element: FocusedElement): DropdownState {
    return state.dropdowns[element];
}

// Helper function to convert state back to instrument
export function stateToInstrument(stateInstrument: SymbolPickerState['instrument']): Instrument | null {
    if (stateInstrument.instrumentType === 'equity') {
        if (!stateInstrument.underlyingSymbol) {
            return null;
        }
        return {
            type: 'equity',
            symbol: stateInstrument.underlyingSymbol,
        };
    } else if (stateInstrument.instrumentType === 'options') {
        if (
            !stateInstrument.underlyingSymbol ||
            !stateInstrument.expirationDate ||
            !stateInstrument.optionCallPut ||
            stateInstrument.strikePrice === undefined
        ) {
            return null;
        }

        return {
            type: 'option',
            underlyingInstrument: {
                type: 'equity',
                symbol: stateInstrument.underlyingSymbol,
            },
            expiry: LocalDate.parse(stateInstrument.expirationDate, DateTimeFormatter.ofPattern('yyMMdd')),
            right: stateInstrument.optionCallPut === 'C' ? 'call' : 'put',
            strike: stateInstrument.strikePrice,
        };
    }
    return null;
}

// Helper function to create initial state from instrument
export function createInitialState(instrumentString: string, allowedTypes: InstrumentType[]): SymbolPickerState {
    const baseState: SymbolPickerState = {
        ...initialState,
        allowedInstrumentTypes: allowedTypes,
        instrument: {
            ...initialState.instrument,
            instrumentType: allowedTypes.length === 1 ? allowedTypes[0] : 'equity',
        },
        dropdowns: {
            ...initialState.dropdowns,
            instrumentType: {
                ...initialState.dropdowns.instrumentType,
                allItems: allowedTypes,
            },
        },
    };

    if (instrumentString) {
        let instrument: Instrument;
        try {
            instrument = parseInstrument(instrumentString);
        } catch (error) {
            log.error('error parsing instrumentString', { error });
            instrument = parseInstrument(''); // if we fail to parse instrumentString we'll fall back to empty equity instrument
        }

        if (instrument.type === 'equity') {
            // Only set instrumentType to equity if allowed types includes equity
            // This prevents overriding options type when we just have an underlying symbol
            if (allowedTypes.includes('equity')) {
                baseState.instrument.instrumentType = 'equity';
            }
            baseState.instrument.underlyingSymbol = instrument.symbol || '';
        } else if (instrument.type === 'option') {
            baseState.instrument.instrumentType = 'options';
            baseState.instrument.underlyingSymbol = getUnderlyingSymbolFromInstrument(instrument) || '';
            baseState.instrument.expirationDate = instrument.expiry.format(DateTimeFormatter.ofPattern('yyMMdd'));
            baseState.instrument.optionCallPut = instrument.right === 'call' ? 'C' : 'P';
            baseState.instrument.strikePrice = instrument.strike;
        }
    }

    baseState.symbolString = validateStateInstrumentAndProduceSymbolString(baseState.instrument);
    return baseState;
}

// Helper function to find the index of the closest strike price to the underlying price
export function findClosestStrikePriceIndex(strikePrices: number[], underlyingPrice: number): number {
    if (strikePrices.length === 0) {
        return 0;
    }

    let closestIndex = 0;
    let closestDifference = Math.abs(strikePrices[0] - underlyingPrice);

    for (let i = 1; i < strikePrices.length; i++) {
        const difference = Math.abs(strikePrices[i] - underlyingPrice);
        if (difference < closestDifference) {
            closestDifference = difference;
            closestIndex = i;
        }
    }

    return closestIndex;
}
